<script lang="ts" setup>
	import { computed } from 'vue'

	import { useCurrencyFormat } from '@/composables/useCurrencyFormat'
	import useFixedNumber from '@/composables/useFixedNumber'

	import { ServiceChargeDetail } from '../../interfaces/hotelRate'

	interface Props {
		includedInFare?: boolean
		subTotal: string
		nights: number
		totalTaxes: number
		totalTaxesPercentage: number
		currencyCode: string
		requestedCurrency: string
		changeCurrency: boolean
		serviceChargeDetails?: ServiceChargeDetail
		serviceChargeLabel?: string
	}

	const props = defineProps<Props>()

	const nightsLabel = (n: number) => (n > 1 ? 'noches' : 'noche')

	// Backend sends number_format() strings like "1,800.00" — strip commas before parsing
	const parseAmount = (value: string | number | null | undefined): number =>
		Number(String(value ?? 0).replace(/,/g, ''))

	// Tarifa todo-incluido (Hotelbeds/prepago): el neto ya trae impuestos y no se agrega
	// nada sobre el subtotal → mostrar "$0.00 (0.0%)" confunde (parece error o "sin
	// impuestos"). Cuando no hay nada que sumar, lo honesto es decir "incluidos en la tarifa".
	const taxesIncluded = computed(
		() => props.includedInFare === true && parseAmount(props.totalTaxes) === 0 && Number(props.totalTaxesPercentage) === 0
	)

	const perNightServiceChargeLabel = computed(() => {
		if (
			!props.serviceChargeDetails?.perNight ||
			props.serviceChargeDetails.type !== 'monto_fijo' ||
			props.nights <= 0
		) {
			return props.serviceChargeLabel
		}

		const totalAmount = parseAmount(props.serviceChargeDetails.approximateAmount)
		const currency = props.serviceChargeDetails.requestedCurrency ?? ''
		return `${useCurrencyFormat(totalAmount / props.nights)} ${currency}`
	})

	const ivaServiceChargeLabel = computed(() => {
		const detail = props.serviceChargeDetails
		if (!detail || detail.type !== 'monto_fijo') return null

		const currency = detail.requestedCurrency ?? ''

		// Use backend-calculated IVA when available (BookingV2 #586), otherwise derive at 16%
		const ivaTotal = detail.approximateVat != null
			? parseAmount(detail.approximateVat)
			: parseAmount(detail.approximateAmount) * 0.16

		if (detail.perNight && props.nights > 0) {
			return `${useCurrencyFormat(ivaTotal / props.nights)} ${currency}`
		}

		return `${useCurrencyFormat(ivaTotal)} ${currency}`
	})
</script>

<template>
	<div class="bg-gray-50 px-4 lg:px-6 py-3 lg:py-4 border-t border-gray-200">
		<div class="space-y-2">
			<div class="flex justify-between items-center">
				<span class="font-inter text-xs lg:text-sm text-gray-600">
					Subtotal ({{ nights }} {{ nightsLabel(nights) }})
				</span>
				<span class="font-inter font-semibold text-xs lg:text-sm text-gray-900">
					{{ subTotal }}
					{{ changeCurrency ? currencyCode : requestedCurrency }}
				</span>
			</div>

			<div class="flex justify-between items-center">
				<span class="font-inter text-xs lg:text-sm text-gray-600">
					Impuestos y cargos
				</span>
				<span class="font-inter font-semibold text-xs lg:text-sm text-gray-900">
					<template v-if="taxesIncluded">Incluidos en la tarifa</template>
					<template v-else>
						({{ Number(totalTaxesPercentage).toFixed(1) }}%)
						${{ useFixedNumber(totalTaxes).fixedNumber }}
						{{ changeCurrency ? currencyCode : requestedCurrency }}
					</template>
				</span>
			</div>
		</div>

		<!-- Service charge note -->
		<div
			v-if="serviceChargeDetails"
			class="mt-3 lg:mt-4 pt-2 lg:pt-3 border-t border-gray-200"
		>
			<p class="text-xs text-gray-600 font-inter italic">
				* El total incluye un cargo por servicio de
				{{ perNightServiceChargeLabel }}
				<template v-if="ivaServiceChargeLabel">
					+ {{ ivaServiceChargeLabel }} IVA
				</template>
				{{ serviceChargeDetails?.perNight && nights > 0 ? 'por noche' : 'por estancia' }}.
			</p>
		</div>
	</div>
</template>
